import React, { forwardRef, useEffect, useState } from 'react';

import moment from 'moment';

import { formatPeriodDate, getSummaryOfGroupings } from '../../../../helpers/utils/functions';
import { prepareRows } from '../../../../helpers/utils/reports';
import { MetricsPaths } from '../../../../helpers/utils/enums';
import { showErrorMessage } from '../../../../helpers/utils/ui';
import { unApi } from '../../../../shared/common/api/endpoints/reports/unApi';
import Loader from '../../../../shared/common/components/loader/Loader';
import { moneyFormatGroup, percentFormatGroup } from '../../../../shared/common/constants/groups';
import { useTypedSelector } from '../../../../shared/common/hooks/useTypedSelector';
import {
  getReportTypeFromDashboardDate,
  mergeAndBuildApiFilters,
} from '../../../../shared/common/components/reports/params/filter/apiFilters';
import styles from './Card.module.scss';

const CartNumber = forwardRef((props: any, ref: any) => {
  const metricName = props.metricsChecked[0]?.label || 'Выберите метрику';
  const period = useTypedSelector((state) => state.period);
  const [ value, setValue ] = useState('');
  // Строки и итог последнего ответа сохраняем, чтобы пересчитывать число
  // при изменении выбранных галочек без повторного запроса к API.
  const [ tableRows, setTableRows ] = useState<any[]>([]);
  const [ totalRow, setTotalRow ] = useState<any>(null);
  const { isAllBlocksFetching } = useTypedSelector((state) => state.meta);
  const { isTableFetching } = useTypedSelector((state) =>
    props?.blockId ? state.meta.blocks[props.blockId] || { isTableFetching: false } : state.meta
  );
  const reportType = getReportTypeFromDashboardDate(props.date);
  // Общие фильтры дашборда — по типу отчёта.
  const commonFilters = useTypedSelector(
    (state) => state.filter.dashboardCommonByReportType?.[reportType] ?? state.filter.filters
  );
  const { attributesDashboard } = useTypedSelector((state) => state.attributes);
  const [ isLoading, setLoading ] = useState(false);
  const { filters: filterState } = useTypedSelector((state) => state.filter);
  const { schoolDetail } = useTypedSelector((state) => state.currentSchool);

  const loadRows = (loadCurrent?: any) => {
    setLoading(true);

    type DateKeys = 'reg' | 'ad' | 'order' | 'payment' | 'web' | 'visit';

    const dateToPathname: Record<DateKeys, MetricsPaths> = {
      reg: MetricsPaths.Universal,
      ad: MetricsPaths.AdReports,
      order: MetricsPaths.OrderReports,
      payment: MetricsPaths.PaymentReports,
      web: MetricsPaths.WebinarReports,
      visit: MetricsPaths.Universal,
    };

    let __attribution = (
      attributesDashboard?.attribution[0] === 'blocks'
        ? props?.attribution
        : attributesDashboard
    )?.attribution;
    let __type_app = (
      attributesDashboard?.type_applications[0] === 'blocks'
        ? props?.attribution
        : attributesDashboard
    )?.type_applications;
    let __period = period;

    const _period = {
      start: formatPeriodDate(__period.start, __period.withTime),
      end: formatPeriodDate(__period.end, __period.withTime),
    };
    const groupings_names = (props.selectedGroupings || props.groupings || []).map(
      (col: any) => col.dataKey
    );
    const metrics_names = (props.selectedMetrics || props.metrics || []).map(
      (col: any) => col.dataKey
    );
    const expanded_ids = loadCurrent ? loadCurrent.clientData.path : [];

    if (expanded_ids.length > 0 && expanded_ids.length === groupings_names.length) {
      setLoading(false);
      return;
    }
    let data = {
      period: _period,
      filters: mergeAndBuildApiFilters({
        blockFilters: props.filters,
        globalDashboardFilters: commonFilters,
        isDashboardBlock: !!props?.isShow,
      }),
      attribution: {
        attribution:
          typeof __attribution === 'string' ? [ __attribution ] : __attribution,
        type_applications:
          typeof __type_app === 'string' ? [ __type_app ] : __type_app,
      },
      groupings_names,
      metrics_names,
      expanded_ids,
    };

    window.localStorage.setItem('prevRequestState', JSON.stringify(data));
    unApi
      .getUniversalReportTableData(
        data,
        dateToPathname[props.date as DateKeys]
      )
      .then((response) => {
        const rowsWithIdAsString = response.data?.rows?.map((row: any) => {
          if (row.id) {
            return { ...row, id: row.id.toString() };
          }
          return row;
        });

        const data = { ...response.data, rows: rowsWithIdAsString };

        if (data.success) {
          // prepareRows добавляет строкам pathStr/clientData — по ним
          // getSummaryOfGroupings находит выбранные строки (см. эффект ниже).
          const preparedRows = prepareRows({
            rows: data.rows || [],
            groupings: props.selectedGroupings || props.groupings || [],
          });
          setTableRows(preparedRows);
          setTotalRow(data.total || null);
        } else {
          setTableRows([]);
          setTotalRow(null);
          setValue('');
          if (response.status === 504) {
            showErrorMessage(
              'Запрос выполняется слишком долго, из-за большого количества данных. \n Можно уменьшить период отчёта, удалить ненужные метрики или фильтры.'
            );
          }
        }
      })
      .catch((error) => {
        setTableRows([]);
        setTotalRow(null);
        setValue('');
        if (error?.response?.status !== 401) {
          showErrorMessage('Ошибка при получении данных');
        }
        if (error.response) {
          if (error.response.status === 504) {
            showErrorMessage(
              'Запрос выполняется слишком долго, из-за большого количества данных. \n Можно уменьшить период отчёта, удалить ненужные метрики или фильтры.'
            );
          }
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // Загрузка данных ТОЛЬКО по флагам fetching (по нажатию кнопки "Показать")
  // или при изменении глобальных фильтров дашборда
  useEffect(() => {
    if (isAllBlocksFetching || isTableFetching) {
      loadRows();
    }
  }, [ isAllBlocksFetching, isTableFetching ]);
  
  // Перезагрузка при изменении глобальных фильтров дашборда (для блоков на дашборде)
  const commonFiltersJson = JSON.stringify(commonFilters);
  useEffect(() => {
    if (props?.isShow && commonFilters && commonFilters.length > 0) {
      loadRows();
    }
  }, [ commonFiltersJson, props?.isShow ]);

  // Перезагрузка при изменении метрики (после редактирования блока)
  const currentMetricKey = props.metricsChecked?.[0]?.dataKey;
  useEffect(() => {
    if (props?.isShow && currentMetricKey) {
      loadRows();
    }
  }, [ currentMetricKey ]);

  // Маппинг кастомных метрик (dataKey начинается с '_') — нужен
  // getSummaryOfGroupings для корректного усреднения формульных метрик.
  const buildCustomMetricsMap = () => {
    const map: Record<string, any> = {};
    (props.metrics || props.selectedMetrics || []).forEach((metric: any) => {
      if (metric?.dataKey?.startsWith('_')) {
        map[metric.dataKey] = {
          base_metric_name: metric.base_metric_name,
          metric_formula: metric.metric_formula,
          unit: metric.unit,
        };
      }
    });
    return map;
  };

  // Вычисляем отображаемое число: если отмечены строки группировки — сумма/среднее
  // по ним (как в строке «Итого и среднее выбранных»), иначе — общий итог.
  const groupsCheckedJson = JSON.stringify(props.groupsChecked || []);
  useEffect(() => {
    const dataKey = props.metricsChecked?.[0]?.dataKey;
    if (!dataKey || !totalRow) {
      return;
    }
    const checked = Array.isArray(props.groupsChecked) ? props.groupsChecked : [];
    if (checked.length > 0 && tableRows.length > 0) {
      const summary = getSummaryOfGroupings(checked, tableRows, buildCustomMetricsMap());
      setValue(summary?.[dataKey] ?? '');
    } else {
      setValue(totalRow?.[dataKey] ?? '');
    }
  }, [ tableRows, totalRow, groupsCheckedJson, currentMetricKey ]);
  const getStylesForBody = () => {
    return props?.isShow
      ? {
        padding: 0,
        jusstifyContent: 'flex-start',
        alignItems: 'flex-start',
      }
      : {
        padding: '10px 20px',
        width: '100%',
        height: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      };
  };
  const getStyles = () => {
    return props?.isShow
      ? {
        padding: 0,
        marginTop: '10px',
        jusstifyContent: 'flex-start',
        alignItems: 'flex-start',
      }
      : {
        borderRadius: '10px',
        height: '100%',
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      };
  };

  const formatValue = (val: any) => {
    if (!val && val !== 0) {
      return <span className={styles.metricEmpty}>—</span>;
    }

    if (typeof val === 'number' || typeof val === 'string') {
      const num = Number(val);
      if (isNaN(num)) return val;

      return Number.isInteger(num)
        ? num.toLocaleString('ru-RU')
        : num.toLocaleString('ru-RU', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        });
    }
    return val;
  };

  const getSuffix = () => {
    const metricDataKey = props.metricsChecked[0]?.dataKey || '';
    // Убираем суффиксы _2, _4 и т.д. для корректной проверки
    const baseMetricKey = metricDataKey.replace(/_\d+$/, '');

    // Проверка на наличие ключевых слов в метке
    const isMoneyMetric = moneyFormatGroup.some((metric) => metric === metricDataKey || metric === baseMetricKey);
    const isPercent = percentFormatGroup.some((metric) => metric === metricDataKey || metric === baseMetricKey);
    return isMoneyMetric ? schoolDetail?.currency.symbol || '₽' : isPercent ? '%' : '';
  };

  // Определяем тип метрики для цветовой индикации
  const getMetricType = () => {
    const metricDataKey = props.metricsChecked[0]?.dataKey || '';
    // Убираем суффиксы _2, _4 и т.д. для корректной проверки
    const baseMetricKey = metricDataKey.replace(/_\d+$/, '');

    if (moneyFormatGroup.some((metric) => metric === metricDataKey || metric === baseMetricKey)) return 'money';
    if (percentFormatGroup.some((metric) => metric === metricDataKey || metric === baseMetricKey)) return 'percent';
    return 'default';
  };

  // Находим первое совпавшее правило (диапазоны от/до)
  const getMatchedRule = () => {
    const rules = props.colorRules;
    if (!rules || rules.length === 0) return undefined;

    const num = Number(value);
    if (isNaN(num)) return undefined;

    for (const rule of rules) {
      const fromOk = rule.from === null || rule.from === undefined || num >= rule.from;
      const toOk = rule.to === null || rule.to === undefined || num <= rule.to;
      if (fromOk && toOk) return rule;
    }
    return undefined;
  };

  // Новый компактный дизайн для режима отображения на дашборде
  if (props?.isShow) {
    const matchedRule = getMatchedRule();
    const ruleStyle: React.CSSProperties = {};
    if (matchedRule?.color) ruleStyle.color = matchedRule.color;
    if (matchedRule?.fontSize) ruleStyle.fontSize = `${matchedRule.fontSize}px`;
    if (matchedRule?.fontWeight) ruleStyle.fontWeight = matchedRule.fontWeight;

    return (
      <div className={styles.metricCard}>
        <div className={styles.metricContent}>
          {isLoading ? (
            <div className={styles.skeleton}>
              <div className={styles.skeletonLine} />
            </div>
          ) : (
            <div
              className={styles.metricValue}
              data-type={matchedRule ? undefined : getMetricType()}
              style={Object.keys(ruleStyle).length > 0 ? ruleStyle : undefined}
            >
              {formatValue(value)}
              {getSuffix() && <span className={styles.metricSuffix}>{getSuffix()}</span>}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Режим превью/модального окна (без изменений в логике)
  return (
    <div className={styles.metricCardPreview}>
      <span className={styles.metricLabelPreview}>{metricName}</span>
      <Loader loading={isLoading}>
        <span className={styles.metricValuePreview}>
          {formatValue(value)} {getSuffix()}
        </span>
      </Loader>
    </div>
  );
});

// Экспортируем без мемоизации, т.к. компонент зависит от многих пропсов
// и мемоизация может блокировать необходимые обновления
export default CartNumber;
